import { Container as ModalContainer } from 'react-modal-promise';
import { BrowserRouter } from 'react-router-dom';
import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider } from '@mui/material/styles';
import { LocalizationProvider } from '@mui/x-date-pickers-pro';
import { AdapterDateFns } from '@mui/x-date-pickers-pro/AdapterDateFns';
import { LicenseInfo } from '@mui/x-license-pro';
import * as Sentry from '@sentry/react';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { SnackbarProvider } from 'notistack';

import { queryClient } from '@/adapters/query';

import { ErrorBoundaryFallback } from './components/ErrorBoundaryFallback';
import { isPublicSignerRoute } from './lib/isPublicSignerRoute';
import { loadGoogleMaps } from './lib/loadGoogleMaps';
import { loadUpscope } from './lib/loadUpscope';
import { Pylon } from './lib/Pylon';

import { AppRoutes } from './AppRoutes';
import theme from './theme';

import './styles.css';

LicenseInfo.setLicenseKey(import.meta.env.VITE_MUI_X_PRO_LICENSE_KEY);

// Skip third-party JS / support / co-browse / chat tooling on the public
// signer route. The URL carries a bearer token; Google Maps Places, Upscope,
// and Pylon all execute third-party JS or capture URL/DOM context that must
// not run on a PHI / signature page.
if (!isPublicSignerRoute()) {
  loadGoogleMaps();
  loadUpscope();
  new Pylon().load();
}

const App = () => {
  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Sentry.ErrorBoundary
          showDialog
          fallback={({ error, resetError }) => (
            <ErrorBoundaryFallback
              error={error as Error}
              resetError={resetError}
            />
          )}
        >
          <BrowserRouter>
            <QueryClientProvider client={queryClient}>
              <SnackbarProvider maxSnack={3} autoHideDuration={6000}>
                <AppRoutes />
                <ModalContainer />
              </SnackbarProvider>
              <ReactQueryDevtools
                toggleButtonProps={{
                  style: {
                    left: 16,
                    bottom: 10
                  }
                }}
              />
            </QueryClientProvider>
          </BrowserRouter>
        </Sentry.ErrorBoundary>
      </ThemeProvider>
    </LocalizationProvider>
  );
};

export default App;
